import { FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel, CommonEntityActiveSwitch } from "../../entities/common";
import { AddComplimentrySubscriptionModel, SubscriptionCommonInfoModel, SubscriptionModel } from "../../entities/subscription";
import { accountingApiSlice, TagType } from "./accountingApiSlice";
import { getQuerySearchParams } from "../../utils";

export const subscriptionApi = accountingApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSubscriptionsCommonInfo: builder.query<SubscriptionCommonInfoModel, undefined>({
      query: () => "/subscription/commonInfo",
      providesTags: [{ type: TagType.Subscription, id: "LIST" }],
    }),

    getSubscriptionsFilteredPaged: builder.query<FilteredPagedQueryResult<SubscriptionModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/subscription/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TagType.Subscription, id: "PARTIAL-LIST" }, ...result.data.map(({ id }) => ({ type: TagType.Subscription, id }))]
          : [{ type: TagType.Subscription, id: "PARTIAL-LIST" }],
    }),

    getSubscription: builder.query<SubscriptionModel, number>({
      query: (subscriptionId) => `/subscription/${subscriptionId}`,
      providesTags: (result, error, arg) => [{ type: TagType.Subscription, id: arg }],
    }),

    addBeginningSubscription: builder.mutation({
      query: (userId: string) => ({
        url: `/subscription/beginning/account/${userId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Subscription, id: "LIST" },
        { type: TagType.Subscription, id: "PARTIAL-LIST" },
      ],
    }),

    addComplimentrySubscription: builder.mutation<void, AddComplimentrySubscriptionModel>({
      query: (model) => ({
        url: "/subscription/complimentry",
        method: "POST",
        body: model
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Subscription, id: "LIST" },
        { type: TagType.Subscription, id: "PARTIAL-LIST" },
      ],
    }),

    switchActiveSubscription: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/subscription/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Subscription, id: arg.id },
        { type: TagType.Subscription, id: "LIST" },
      ],
    }),

    deleteSubscription: builder.mutation({
      query: (subscriptionId: number) => ({
        url: `/subscription/${subscriptionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.Subscription, id: arg },
              { type: TagType.Subscription, id: "LIST" },
            ],
    }),
    deleteMultiSubscriptions: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/subscription/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagType.Subscription,
                id,
              })),
              { type: TagType.Subscription, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetSubscriptionsCommonInfoQuery,
  useGetSubscriptionsFilteredPagedQuery,
  useGetSubscriptionQuery,
  useAddBeginningSubscriptionMutation,
  useAddComplimentrySubscriptionMutation,
  useSwitchActiveSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useDeleteMultiSubscriptionsMutation,
} = subscriptionApi;
